import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Eye, EyeOff, Radio } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ScoutWireManager() {
  const [items, setItems] = useState([]);
  const [newText, setNewText] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/scout-wire/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch {
      toast.error("Failed to load Scout Wire items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const addItem = async () => {
    if (!newText.trim()) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/scout-wire/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: newText.trim() }),
      });
      if (res.ok) {
        toast.success("Item added to Scout Wire");
        setNewText("");
        fetchItems();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add");
      }
    } catch {
      toast.error("Failed to add item");
    }
  };

  const toggleActive = async (item) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/admin/scout-wire/items/${item.item_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !item.is_active }),
      });
      fetchItems();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this Scout Wire item?")) return;
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/admin/scout-wire/items/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      fetchItems();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="scout-wire-manager">
      <div className="flex items-center gap-3 mb-2">
        <Radio size={20} className="text-red-500" />
        <div>
          <h3 className="text-lg font-bold text-gray-900">Scout Wire Ticker</h3>
          <p className="text-sm text-gray-500">Manage the scrolling ticker text on the homepage</p>
        </div>
      </div>

      {/* Add New Item */}
      <div className="flex gap-2">
        <Input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="e.g., Player Name (2026 RHP) touched 92 MPH at showcase..."
          className="flex-1"
          onKeyDown={(e) => e.key === "Enter" && addItem()}
          data-testid="scout-wire-input"
        />
        <Button onClick={addItem} className="bg-red-600 hover:bg-red-700 text-white" data-testid="scout-wire-add-btn">
          <Plus size={16} className="mr-1" /> Add
        </Button>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <Radio size={32} className="text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No Scout Wire items yet. Add your first ticker message above.</p>
          <p className="text-gray-400 text-xs mt-1">Default items will show until you add custom ones.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.item_id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition ${
                item.is_active ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100 opacity-60"
              }`}
              data-testid={`scout-wire-item-${item.item_id}`}
            >
              <GripVertical size={16} className="text-gray-300 flex-shrink-0 cursor-grab" />
              <p className={`flex-1 text-sm ${item.is_active ? "text-gray-800" : "text-gray-400 line-through"}`}>
                {item.text}
              </p>
              <button
                onClick={() => toggleActive(item)}
                className={`p-1.5 rounded-md transition ${item.is_active ? "hover:bg-green-50 text-green-600" : "hover:bg-gray-200 text-gray-400"}`}
                title={item.is_active ? "Active — click to hide" : "Hidden — click to show"}
              >
                {item.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <button
                onClick={() => deleteItem(item.item_id)}
                className="p-1.5 rounded-md hover:bg-red-50 text-red-400 hover:text-red-600 transition"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400">
        Active items appear in the scrolling ticker on the homepage. Toggle visibility or delete items as needed.
      </p>
    </div>
  );
}
